import { queryOptions, useQuery } from '@tanstack/react-query';

import { assistantApiUrl, assistantRequestInit } from '@/lib/assistant-request';
import { queryKeys } from '@/lib/query-keys';

/**
 * The finite set of skill ids a memory can be scoped to (Phase 3,
 * docs/plans/claire-memory-suggestions.md), fetched from
 * `GET /assistant/memory-scopes` rather than hand-maintained here — the
 * backend (`MEMORY_SCOPE_LABELS`) is the single source of truth.
 */
export interface MemoryScopeOption {
  id: string;
  label: string;
}

interface MemoryScopesResponse {
  items: MemoryScopeOption[];
}

export const memoryScopesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.assistant.memoryScopes(),
    queryFn: async (): Promise<MemoryScopesResponse> => {
      const res = await fetch(
        assistantApiUrl('memory-scopes'),
        assistantRequestInit()
      );
      if (!res.ok) throw new Error('Failed to load memory scopes');
      return res.json();
    },
    // Static, org-independent data — safe to keep fresh far longer than the
    // memories list itself.
    staleTime: 60 * 60 * 1000,
  });

export function useMemoryScopes() {
  const query = useQuery(memoryScopesQueryOptions());
  return {
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
